'use client';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { FlickeringGrid } from 'components/performance-lab/leaderboard/FlickeringGrid';
import { useModal } from 'components/shared/Modal';
import AppHandoffModal from 'components/pulsomics/AppHandoffModal';
import {
  openPulsomicsApp,
  pulsomicsJoinLink,
} from 'components/pulsomics/deeplinks';
import pulso from 'components/pulsomics/typography.module.css';
import styles from './ScienceOpening.module.css';

const HeroParticlesScene = dynamic(() => import('./gl/HeroParticlesScene'), {
  ssr: false,
});

/* ScienceOpening — the Pulsomics opening as ONE canvas + ONE scroll timeline:
 * dust → the helix forms → the biomarkers line types in → HOLD. The orb act
 * this timeline was written for belongs to /science and is pinned off here.
 * Reduced-motion / no-WebGL → no canvas at all. */

/* Drafted against the launch feedback: the hero was asked to put the models
   themselves in the first fold. It states what there is to read before the
   page starts explaining how it gets read; "One pulse" was dropped from it
   because the Pulse-Omics heading below already owns that phrase. */
const SCRUB_LINE =
  'Billions of heartbeats. One structure running through them all.';

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const smooth = (a: number, b: number, x: number) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

/* Pulsomics: the helix forms over the first stretch of the track and then
   holds, leaving room for the biomarkers line to type in and settle before the
   section ends.

   Two end points, because the two tracks are not the same length. The narrow
   one is 240vh, so 0.45 leaves about as much hold after the helix lands as it
   spent forming it — a beat. The wide one was 400vh against the same fraction,
   which left more than a screen and a half of scrolling against a finished
   image, and that is the part that read as a threshold rather than a beat.
   Ending the assembly at 0.58 there spends the scroll on the forming instead
   of after it. See the .track comment for the numbers. */
const WAVE_A = 0.05,
  WAVE_B = 0.45,
  WAVE_B_WIDE = 0.58;

const useCanWebGL = () => {
  const [ok, setOk] = useState<boolean | null>(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setOk(false);
      return;
    }
    try {
      const c = document.createElement('canvas');
      setOk(!!(c.getContext('webgl2') || c.getContext('webgl')));
    } catch {
      setOk(false);
    }
  }, []);
  return ok;
};

const ScienceOpening = () => {
  const canGL = useCanWebGL();
  const ModalClient = useModal();

  /* The same handoff the section CTAs use, and the same one this hero had
     before the DNA opening replaced it: a phone tries the app and falls back
     to its store, desktop gets the QR dialog. Joining happens in the app
     either way, so an href that merely navigates would be a dead end — which
     is what `href="/pulsomics"` had quietly become, a link from the page back
     to itself. The href stays as a real target for middle-click and for
     no-JS, and the handler takes over otherwise. */
  const handleJoin = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (openPulsomicsApp(pulsomicsJoinLink())) return;
      ModalClient.set(
        <AppHandoffModal
          heading="Join Pulsomics in the app"
          text="Scan to install it on your phone."
        />,
      );
      ModalClient.show();
    },
    [ModalClient],
  );
  const trackRef = useRef<HTMLDivElement | null>(null);
  const stickyRef = useRef<HTMLElement | null>(null);

  // canvas-driven refs
  const progressRef = useRef(0);
  const fadeRef = useRef(1);
  const collapseRef = useRef(0);
  const implodeRef = useRef(0);

  // eased follow + overlay refs
  const dispRef = useRef(0);
  const headBottomRef = useRef(0);
  const beatARef = useRef<HTMLDivElement | null>(null);
  const beatBRef = useRef<HTMLDivElement | null>(null);
  const underRef = useRef<HTMLDivElement | null>(null);
  const hintRef = useRef<HTMLDivElement | null>(null);
  /* Last scroll, for the helix shimmer on narrow viewports: it only fires once
     the reader has stopped. Seeded at mount so a visitor who lands and never
     scrolls still gets it. */
  const idleSinceRef = useRef(0);

  /* Measures the header onto the stage, for the Beat's top padding. Re-read on
     resize and once after mount, since the banner can arrive late. */
  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return undefined;
    const measure = () => {
      const header = document.querySelector('header');
      const bottom = header
        ? Math.max(header.getBoundingClientRect().bottom, 0)
        : 0;
      /* Also kept for the scroll loop's clip-path, which used to re-query and
         re-measure the header on every frame. It cannot change mid-scroll, and
         this already re-runs on resize and after the late banner. */
      headBottomRef.current = bottom;
      el.style.setProperty('--hero-head', `${Math.round(bottom)}px`);
    };
    measure();
    const late = window.setTimeout(measure, 600);
    window.addEventListener('resize', measure);
    return () => {
      window.clearTimeout(late);
      window.removeEventListener('resize', measure);
    };
  }, []);

  const words = SCRUB_LINE.split(' ');
  const perWord = 1 / Math.max(words.length, 1);
  /* The scrubbed words are written straight to style from the rAF loop, the
     way WordScrub does it in atomsClient.tsx. Holding the progress in React
     state re-rendered this whole section — and, through it, re-configured the
     R3F root — on every frame of every scroll. */
  const wordElsRef = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    if (!canGL) return;
    const el = trackRef.current;
    if (!el) return;
    let raf = 0;
    idleSinceRef.current = performance.now();

    /* Which track is on screen. Read live rather than captured, so a resize
       across the breakpoint picks up the other end point on the next scroll —
       the resize listener below already re-reads everything. */
    const wide = window.matchMedia('(min-width: 992px)');

    const readTarget = () => {
      const r = el.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      const raw = clamp01(total > 0 ? -r.top / total : 0);
      return { r, raw };
    };

    const render = (r: DOMRect, d: number) => {
      // clip-path box: expand from the boxed rest state on scroll-in
      if (stickyRef.current) {
        const side = window.innerWidth < 768 ? 8 : 12;
        /* Intro only. The section opens as an inset card and releases to
           full-bleed as the helix forms; it does not box itself again on the
           way out. Closing back into a rounded rectangle made the hand-off to
           the next section read as the page retreating, and full-length is
           where the eye already is by then. */
        const g = 1 - smooth(0.004, 0.06, d);
        const stickyTop = Math.max(r.top, 0);
        const top = Math.max(headBottomRef.current - stickyTop, 0) + side;
        stickyRef.current.style.clipPath = `inset(${top * g}px ${side * g}px ${
          side * g
        }px ${side * g}px round ${28 * g}px)`;
      }

      /* The wave assembles over the first stretch of the track and then holds
         for the rest, so there is a beat where it is simply there. */
      const waveB = wide.matches ? WAVE_B_WIDE : WAVE_B;
      /* How far through the assembly we are. The overlays below are timed
         against this rather than against d, so their choreography holds at
         both track lengths instead of drifting when the end point moves. */
      const w = clamp01((d - WAVE_A) / (waveB - WAVE_A));
      progressRef.current = w;
      /* Pinned: the wave never disperses and never hands to an orb. */
      collapseRef.current = 0;
      fadeRef.current = 1;

      /* The dotted grid used to arrive with the orb. With the timeline capped
         at the hold that never fires, so it comes in with the wave and stays:
         it is this section's background now. */
      if (underRef.current)
        underRef.current.style.opacity = String(smooth(0.02, waveB, d));

      // overlays
      if (beatARef.current) {
        /* The hero line clears as the helix assembles, handing over to the
           biomarkers line which then holds for the rest of the section. */
        const o = 1 - smooth(0.125, 0.525, w);
        beatARef.current.style.opacity = String(o);
        beatARef.current.style.transform = `translateY(${d * -70}px)`;
        beatARef.current.style.pointerEvents = o > 0.5 ? 'auto' : 'none';
      }
      if (beatBRef.current) {
        /* Types in as the helix forms and then stays. No fade at the
           threshold: on /science that cleared it for the orb, and this
           section ends on the helix instead. */
        beatBRef.current.style.opacity = String(smooth(0.425, 0.925, w));
        beatBRef.current.style.pointerEvents = 'none';
      }
      if (hintRef.current)
        hintRef.current.style.opacity = String(1 - smooth(0.02, 0.08, d));

      /* Beat B's thesis line, word by word. Same arithmetic as the inline
         style it replaces, so the fade is unchanged. */
      const els = wordElsRef.current;
      for (let k = 0; k < els.length; k += 1) {
        const wordEl = els[k];
        if (!wordEl) continue;
        const local = Math.min(
          1,
          Math.max(0, ((d - 0.24) / 0.2 - k * perWord * 0.9) / perWord),
        );
        wordEl.style.opacity = String(0.14 + 0.86 * local);
      }
    };

    const EASE = 0.12; // damp fast/flick scrolls; slow scroll stays ~1:1
    const loop = () => {
      const { r, raw } = readTarget();
      const diff = raw - dispRef.current;
      const next =
        Math.abs(diff) < 0.0006 ? raw : dispRef.current + diff * EASE;
      dispRef.current = next;
      render(r, next);
      raf = next === raw ? 0 : requestAnimationFrame(loop);
    };
    const onScroll = () => {
      /* Every scroll resets the shimmer's idle clock. Cheap enough for the raw
         event: one assignment, no layout read. */
      idleSinceRef.current = performance.now();
      if (!raf) raf = requestAnimationFrame(loop);
    };

    const first = readTarget();
    dispRef.current = first.raw;
    render(first.r, first.raw);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [canGL]);

  return (
    <div className={styles.track} ref={trackRef}>
      <section className={styles.sticky} ref={stickyRef}>
        <div className={styles.glLayer}>
          {canGL ? (
            <HeroParticlesScene
              idleSinceRef={idleSinceRef}
              progressRef={progressRef}
              fadeRef={fadeRef}
              collapseRef={collapseRef}
              implodeRef={implodeRef}
            />
          ) : null}
        </div>

        {/* Dotted grid fades in with the wave. .sticky already paints the
            stage colour, so this layer stays transparent over the helix. */}
        <div
          className={styles.under}
          ref={underRef}
          style={{ opacity: 0 }}
          aria-hidden="true"
        >
          {/* ChapterScene removed: on /science it is the orb section's
              atmosphere, but here it is a second full-size particle canvas
              and its dust sits over the wave. The dotted grid below is the
              backdrop this section keeps. */}
          <div className={styles.gridFx}>
            <FlickeringGrid
              squareSize={3}
              gridGap={9}
              flickerChance={0.28}
              color="#6d9bff"
              maxOpacity={0.26}
              edgeMaxOpacity={0}
              fadePower={1.6}
            />
          </div>
        </div>

        {/* The orb is removed, not hidden: Pulsomics ends this section on the
            formed helix and never hands over to a sphere. */}

        {/* Beat A — the thesis */}
        <div className={styles.beat} ref={beatARef}>
          <p className={styles.eyebrow}>Pulsomics</p>
          <h1 className={`${pulso.display} ${styles.h1}`}>
            A foundational model of the human pulse
          </h1>
          <p className={styles.sub}>
            Every system in the body leaves its trace on the pulse. We&apos;re
            building the model that reads them all.
          </p>
          <div className={styles.ctas}>
            <Link
              className={pulso.pillSolidOnDark}
              href="#how-it-works"
              onClick={handleJoin}
              aria-label="Join Pulsomics in the Ultrahuman app"
            >
              Join Pulsomics
            </Link>
          </div>
        </div>

        {/* Beat B — the scrubbed thesis line that holds at the threshold */}
        <div
          className={styles.beat}
          ref={beatBRef}
          style={{ opacity: 0 }}
          aria-hidden="true"
        >
          <p className={styles.scrub}>
            {words.map((w, k) => (
              <span
                key={`${w}-${k}`}
                ref={(el) => {
                  wordElsRef.current[k] = el;
                }}
              >
                {w}{' '}
              </span>
            ))}
          </p>
        </div>

        {/* The orb UI is removed with it. It carried the recovery-state copy
            and the heart-rate / recovery / sleep / movement read-outs, none of
            which belong to this section. */}

        <div className={styles.scrollHint} ref={hintRef} aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 4v15M6 13l6 6 6-6"
              stroke="rgba(255,255,255,0.7)"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </section>
    </div>
  );
};

export default ScienceOpening;
